import { nativeStcPayload, nativeSrpPayload } from '~/native/home/utils';
import FinancingInfo from './DynamicPromoTile/FinancingInfo';
import TradeInfo from './DynamicPromoTile/TradeInfo';

export enum TileType {
  Finance = 'Finance',
  Trade = 'Trade',
  Value = 'Value',
  Insurance = 'Insurance'
}

export enum TileStatus {
  isActive = 'isActive',
  isExpired = 'isExpired',
  isPending = 'isPending',
  isWarning = 'isWarning'
}

export function getTileTitle(tileType: TileType) {
  const tileTitles = {
    [TileType.Finance]: 'Finance with Carvana',
    [TileType.Trade]: 'Sell or trade your car',
    [TileType.Value]: "Stay up to speed on your car's value",
    [TileType.Insurance]: 'Insurance coverage in 3 clicks™'
  };

  return tileTitles[tileType] || null;
}

export function getTileCopy(tileType: TileType) {
  const tileCopies = {
    [TileType.Value]:
      "Get personalized insights on your car's value and track it over time with Carvana Value Tracker.",
    [TileType.Insurance]:
      'Effortless to buy and personalized to you, introducing Carvana Insurance.'
  };

  return tileCopies[tileType] || null;
}

export function getTileBtnInfo(tileType: TileType): {
  btnText: string;
  btnLink: string;
} {
  const tileBtnInfos = {
    [TileType.Value]: { btnText: 'Learn more', btnLink: '/value-tracker' },
    [TileType.Insurance]: { btnText: 'Learn more', btnLink: '/insurance' }
  };

  return tileBtnInfos[tileType] || { btnText: '', btnLink: '#' };
}

export function getTileImgSrc(tileType: TileType) {
  const tileImgSrcs = {
    [TileType.Finance]: `${process.env.CVNA_APP_CDN_URL}/home-assets/redesign/finance-promo_103023.png`,
    [TileType.Trade]: `${process.env.CVNA_APP_CDN_URL}/home-assets/redesign/trade-promo_103023.png`,
    [TileType.Value]: `${process.env.CVNA_APP_CDN_URL}/home-assets/nba/promo_cvt.png`,
    [TileType.Insurance]: `${process.env.CVNA_APP_CDN_URL}/home-assets/redesign/insurance-promo_103023.png`
  };

  return tileImgSrcs[tileType] || undefined;
}

type DynamicTileProps = {
  tileType: TileType;
  tileStatus: TileStatus | null;
};

export function getDynamicTileCopy({ tileType, tileStatus }: DynamicTileProps) {
  const tileMessages = {
    [`${TileType.Finance}`]:
      'No credit impact, view real payments while shopping.',
    [`${TileType.Finance}_${TileStatus.isActive}`]:
      'View real payments while shopping.',
    [`${TileType.Finance}_${TileStatus.isWarning}`]:
      'Your terms will expire soon!',
    [`${TileType.Finance}_${TileStatus.isExpired}`]:
      'Your terms are expired! Refresh your terms today.',
    [`${TileType.Trade}`]: 'Get a real offer in under 2 minutes.',
    [`${TileType.Trade}_${TileStatus.isActive}`]:
      'Shop with your trade-in offer applied.',
    [`${TileType.Trade}_${TileStatus.isWarning}`]:
      'Act now before your offer expires!',
    [`${TileType.Trade}_${TileStatus.isExpired}`]:
      'Answer a few questions to get an updated offer today.',
    [`${TileType.Trade}_${TileStatus.isPending}`]:
      "We're working on your offer."
  };

  return (
    tileMessages[`${tileType}_${tileStatus}`] || tileMessages[tileType] || null
  );
}

export function getDynamicTileBtnInfo({
  tileType,
  tileStatus
}: DynamicTileProps): {
  btnText: string;
  btnLink: string;
  nativePayload?: string;
} {
  const tileButtonInfo = {
    [`${TileType.Finance}`]: {
      btnText: 'Get pre-qualified',
      btnLink: '/financing'
    },
    [`${TileType.Finance}_${TileStatus.isActive}`]: {
      btnText: 'Shop with your terms',
      btnLink: '/cars',
      nativePayload: nativeSrpPayload
    },
    [`${TileType.Finance}_${TileStatus.isWarning}`]: {
      btnText: 'Shop with your terms',
      btnLink: '/cars',
      nativePayload: nativeSrpPayload
    },
    [`${TileType.Finance}_${TileStatus.isExpired}`]: {
      btnText: 'Refresh my Terms',
      btnLink: '/financing'
    },
    [`${TileType.Trade}`]: {
      btnText: 'Get my offer',
      btnLink: '/sell-my-car',
      nativePayload: nativeStcPayload
    },
    [`${TileType.Trade}_${TileStatus.isActive}`]: {
      btnText: 'Shop with my offer',
      btnLink: '/cars',
      nativePayload: nativeSrpPayload
    },
    [`${TileType.Trade}_${TileStatus.isExpired}`]: {
      btnText: 'Refresh My Offer',
      btnLink: '/sell-my-car',
      nativePayload: nativeStcPayload
    },
    [`${TileType.Trade}_${TileStatus.isWarning}`]: {
      btnText: 'Shop with my offer',
      btnLink: '/cars',
      nativePayload: nativeSrpPayload
    },
    [`${TileType.Trade}_${TileStatus.isPending}`]: {
      btnText: 'Shop now',
      btnLink: '/cars',
      nativePayload: nativeSrpPayload
    }
  };

  return (
    tileButtonInfo[`${tileType}_${tileStatus}`] ||
    tileButtonInfo[tileType] || { btnText: '', btnLink: '#' }
  );
}

export function getPillColorScheme(tileStatus: TileStatus) {
  const statusToColorScheme = {
    [TileStatus.isActive]: 'bg-green-1 text-blue-6',
    [TileStatus.isExpired]: 'bg-red-1 text-red-600',
    [TileStatus.isPending]: 'bg-blue-1 text-blue-6',
    [TileStatus.isWarning]: 'bg-yellow-1 text-blue-6'
  };

  return statusToColorScheme[tileStatus];
}

export function getDynamicTilePillText({
  tileType,
  tileStatus
}: DynamicTileProps) {
  const tilePillTexts = {
    [`${TileType.Finance}_${TileStatus.isActive}`]: "You're pre-qualified!",
    [`${TileType.Finance}_${TileStatus.isWarning}`]: 'About to expire',
    [`${TileType.Finance}_${TileStatus.isExpired}`]: 'Expired terms',
    [`${TileType.Trade}_${TileStatus.isActive}`]: 'Offer active',
    [`${TileType.Trade}_${TileStatus.isWarning}`]: 'About to expire',
    [`${TileType.Trade}_${TileStatus.isExpired}`]: 'Offer expired',
    [`${TileType.Trade}_${TileStatus.isPending}`]: 'Offer pending'
  };

  return tilePillTexts[`${tileType}_${tileStatus}`] || null;
}

export function getDynamicTileMainContent({
  tileType,
  tileStatus
}: DynamicTileProps) {
  if (!tileStatus) return null;

  const components = {
    [TileType.Finance]: (
      <FinancingInfo tileType={tileType} tileStatus={TileStatus[tileStatus]} />
    ),
    [TileType.Trade]: (
      <TradeInfo tileType={tileType} tileStatus={TileStatus[tileStatus]} />
    )
  };

  return components[tileType] || null;
}
